import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useOnboarding } from './ImportJobContext';
import { FIRST_TASKS, FIRST_TASK_EVENT, markFirstTask, taskState, tasksFinished, tasksDone } from './firstTaskRegistry';
import { IconTick, IconClose, IconWarn, IconChevron } from './icons';
import toast from '../shared/Toast';
import { useStripeStatus } from '../settings/stripeStatus';
import { useSelector } from 'react-redux';
import siteSetting from '../../config/env';
import './FirstTasks.css';
import { BAILEY_INTRO_ENABLED, INTRO_START_EVENT, isIntroDone } from './bailey-intro/introStage';
import { SETUP_V2_ENABLED } from './flags';
import { openSetup as openSetupV2 } from './setup/SetupContext';

/**
 * The diary card for guided first tasks (replaces the five-row checklist).
 *
 * Also owns the ?setup=1 entry: the post-signup redirect opens the layer
 * once, then the URL is cleaned. A salon whose status is still 'new' is
 * never interrupted (existing businesses, or every fresh mock session).
 *
 * States, in order:
 *   tasks pending  → one task at a time, "1 of 4", Show me / Skip
 *   tasks finished → "Ready to take bookings?" with Go live
 *   live done      → nothing
 *
 * The card gets out of the way of what it teaches: whenever a diary popover,
 * menu or slide panel is open it shrinks to a pill ("Try it · 1 of 4"), and
 * it grows back when that closes. While expanded it pulses the real control
 * the current task is about.
 */

// Anything on screen that the owner is working in. The card collapses while
// one of these exists.
const OVERLAY_SELECTOR = '.dv2-slot-popup, .dv2-staff-popup, .dv2-context-menu, .dv2-fab-menu, .sp-overlay, .ob-root';

function useOverlayOpen(enabled) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled || typeof MutationObserver === 'undefined') return undefined;
    let frame = null;
    const check = () => {
      frame = null;
      setOpen(!!document.querySelector(OVERLAY_SELECTOR));
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(check); };
    check();
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { mo.disconnect(); if (frame != null) cancelAnimationFrame(frame); };
  }, [enabled]);
  return open;
}

/** Adds a soft pulse ring to the live control for as long as `selector` is set. */
function usePulse(selector) {
  useEffect(() => {
    if (!selector) return undefined;
    let el = null;
    const apply = () => {
      const next = document.querySelector(selector);
      if (next === el) return;
      if (el) el.classList.remove('oft-pulse');
      el = next;
      if (el) el.classList.add('oft-pulse');
    };
    apply();
    const id = setInterval(apply, 800); // the FAB remounts with the diary
    return () => { clearInterval(id); if (el) el.classList.remove('oft-pulse'); };
  }, [selector]);
}

export default function FirstTasks() {
  const ob = useOnboarding();
  const history = useHistory();
  const location = useLocation();
  const openedRef = useRef(false);
  const [hidden, setHidden] = useState(false);
  const [flash, setFlash] = useState(null); // key of the task just completed, for the tick beat
  const [undoing, setUndoing] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false); // owner tapped the pill while something is open
  // Which task the card is looking at. null follows real progress; setting it
  // lets the owner walk back through steps they skipped or finished, so Skip
  // is no longer a one way door.
  const [viewKey, setViewKey] = useState(null);
  const { enabled, onboarding, job, openLayer, patchOnboarding, rollback } = ob || {};
  const active = !!(enabled && onboarding && !hidden && onboarding.status !== 'new'
    && !(onboarding.checklist || {}).hidden && (onboarding.checklist || {}).live !== 'done');
  const overlayOpen = useOverlayOpen(active);
  useEffect(() => { if (!overlayOpen) setPinnedOpen(false); }, [overlayOpen]);
  const collapsed = overlayOpen && !pinnedOpen;
  const stripe = useStripeStatus(null, active);
  // The client-facing booking page, for the "See what clients see" task. The
  // page renders while online booking is still off: the menu, team and photos
  // are all there, and the Book button reads as unavailable until go live.
  const salon = useSelector((s) => (s.user && s.user.userInfo && s.user.userInfo.hairdresser && s.user.userInfo.hairdresser.salon)) || {};
  const bookingLink = `${siteSetting.api.weburl}book/${salon.slug || salon._id || localStorage.getItem('salonId') || ''}`;
  const bookingsLive = salon.onlineBookingEnabled !== false;

  // ?setup=1 (post-signup redirect) is the only automatic open.
  // With Set up with Bailey, wait for the server's copy: it remembers a
  // welcome finished on another device or before signing out.
  const waitForServer = SETUP_V2_ENABLED && !(ob && ob.onboardingLoaded);
  useEffect(() => {
    if (!enabled || openedRef.current || !onboarding || waitForServer) return;
    const params = new URLSearchParams(location.search);
    if (params.get('setup') === '1') {
      openedRef.current = true;
      params.delete('setup');
      history.replace({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' });
      const welcomeDone = SETUP_V2_ENABLED && !!(onboarding.checklist && onboarding.checklist.welcomeDone);
      // Bailey's welcome comes first and opens setup itself when it ends.
      if (BAILEY_INTRO_ENABLED && !isIntroDone() && !welcomeDone) {
        window.dispatchEvent(new CustomEvent(INTRO_START_EVENT, { detail: { handoff: true } }));
      } else if (SETUP_V2_ENABLED) {
        openSetupV2({ from: 'signup' });
      } else {
        openLayer({ mode: 'setup' });
      }
    }
  }, [enabled, onboarding, waitForServer, location, history, openLayer]);

  const checklist = (onboarding && onboarding.checklist) || {};
  const tasks = taskState(checklist);
  const currentTask = FIRST_TASKS.find((t) => !['done', 'skipped'].includes(tasks[t.key]));
  usePulse(active && !collapsed && !flash && checklist.live !== 'later' && currentTask ? currentTask.target : null);
  const setTask = useCallback((key, value) => {
    // The checklist merge is shallow, so always send the whole tasks map.
    patchOnboarding({ checklist: { tasks: { ...taskState((onboarding || {}).checklist), [key]: value } } });
  }, [onboarding, patchOnboarding]);

  // The provider records completions (from any page); this only plays the
  // tick beat when one lands while the card is on screen.
  useEffect(() => {
    if (!enabled) return undefined;
    const on = (e) => {
      const key = e.detail && e.detail.key;
      if (!key || !onboarding || onboarding.status === 'new') return;
      if (taskState(onboarding.checklist)[key] === 'done') return;
      setFlash(key);
      setViewKey(null);
      setTimeout(() => setFlash(null), 1400);
    };
    window.addEventListener(FIRST_TASK_EVENT, on);
    return () => window.removeEventListener(FIRST_TASK_EVENT, on);
  }, [enabled, onboarding]);

  // Set up with Bailey has its own go-live, and its pill.
  if (SETUP_V2_ENABLED) return null;
  if (!enabled || !onboarding || hidden) return null;
  if (onboarding.status === 'new' || checklist.hidden) return null;
  if (checklist.tour === 'pending') return null; // the walk round the diary has the floor
  if (checklist.live === 'done') return null;

  const hide = () => { setHidden(true); patchOnboarding({ checklist: { hidden: true } }); };
  const canUndo = job && job.status === 'committed';
  const undo = async () => {
    if (!window.confirm('Undo this import? Everything it added will be removed. Anything you have edited since stays.')) return;
    setUndoing(true);
    try { await rollback(); toast.success('Import undone'); }
    catch (e) { toast.error(e.message || 'Could not undo. Please try again.'); }
    setUndoing(false);
  };

  // Went through go-live but kept it private: one quiet row until they switch it on.
  if (checklist.live === 'later') {
    return (
      <section className="oft oft--compact" aria-label="Take bookings live">
        <div className="oft-compact-row">
          <span className="oft-compact-dot" aria-hidden="true" />
          <span className="oft-compact-text"><b>Booking page is off.</b> Clients cannot book online yet.</span>
          <button type="button" className="oft-btn oft-btn--primary oft-btn--xs" onClick={() => openLayer({ mode: 'setup', screen: 'live' })}>Go live</button>
          <button type="button" className="oft-hide" onClick={hide} aria-label="Hide"><IconClose size={13} /></button>
        </div>
      </section>
    );
  }

  const finished = tasksFinished(checklist);
  const current = currentTask;
  const viewed = viewKey ? FIRST_TASKS.find((t) => t.key === viewKey) : null;
  const showing = flash ? FIRST_TASKS.find((t) => t.key === flash) : (viewed || current);
  const index = FIRST_TASKS.findIndex((t) => t.key === (showing || {}).key);
  const status = showing ? tasks[showing.key] : null;
  const goTo = (i) => { if (i >= 0 && i < FIRST_TASKS.length) setViewKey(FIRST_TASKS[i].key); };
  const skip = (key) => { setTask(key, 'skipped'); setViewKey(null); };
  const putBack = (key) => { setTask(key, 'todo'); setViewKey(null); };
  const done = tasksDone(checklist);

  // Straight after the practice checkout: the moment to connect payments, so
  // the split at the till works for real. Only when nothing is set up yet.
  const askStripe = tasks.pay === 'done' && !checklist.stripeAsk && !flash
    && !stripe.loading && !stripe.demo && stripe.key === 'not_started';
  const dismissStripe = () => patchOnboarding({ checklist: { stripeAsk: 'later' } });
  const connectStripe = () => { patchOnboarding({ checklist: { stripeAsk: 'opened' } }); history.push('/settings/payout-account'); };

  if (collapsed) {
    return (
      <button type="button" className="oft oft--pill" onClick={() => setPinnedOpen(true)} aria-label="Show setup tips">
        <span className="oft-pill-dot" aria-hidden="true" />
        <span>{finished ? 'Ready to go live' : `Try it · ${index + 1} of ${FIRST_TASKS.length}`}</span>
      </button>
    );
  }

  if (askStripe) {
    return (
      <section className="oft" aria-label="Connect payments">
        <header className="oft-head">
          <span className="oft-eyebrow">Nice. Payment taken</span>
          <button type="button" className="oft-hide" onClick={dismissStripe} aria-label="Later"><IconClose size={13} /></button>
        </header>
        <div className="oft-body">
          <div className="oft-text">
            <div className="oft-title">Want the split at the till?</div>
            <div className="oft-copy">Connect payments and card, Tap to Pay, payment link, Klarna and Clearpay payments split themselves. About five minutes.</div>
          </div>
        </div>
        <div className="oft-actions oft-actions--end">
          <button type="button" className="oft-btn oft-btn--ghost" onClick={dismissStripe}>Later</button>
          <button type="button" className="oft-btn oft-btn--primary" onClick={connectStripe}>Connect payments</button>
        </div>
      </section>
    );
  }

  return (
    <section className={`oft${flash ? ' oft--flash' : ''}`} aria-label={finished ? 'Go live' : 'Try it'}>
      <header className="oft-head">
        {!finished && !flash && (
          <span className="oft-nav">
            <button type="button" className="oft-nav-btn" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous step">
              <IconChevron size={13} />
            </button>
            <button type="button" className="oft-nav-btn oft-nav-btn--next" onClick={() => goTo(index + 1)} disabled={index === FIRST_TASKS.length - 1} aria-label="Next step">
              <IconChevron size={13} />
            </button>
          </span>
        )}
        <span className="oft-eyebrow">{finished ? 'You know your way around' : `Try it · ${index + 1} of ${FIRST_TASKS.length}`}</span>
        <button type="button" className="oft-hide" onClick={hide} aria-label="Hide"><IconClose size={13} /></button>
      </header>

      {!finished && showing && (
        <>
          <div className="oft-body">
            <span className={`oft-mark${flash ? ' is-done' : ''}`} aria-hidden="true"><IconTick size={12} /></span>
            <div className="oft-text">
              <div className="oft-title">{flash ? 'Done' : showing.title}</div>
              <div className="oft-copy">{flash ? 'Nice. Next one.' : showing.body}</div>
              {!flash && showing.external === 'bookingPage' && !bookingsLive && (
                <div className="oft-hint">Your page is not switched on yet, so the Book button will read as unavailable. Everything else is exactly what clients see.</div>
              )}
              {!flash && showing.hint && !(showing.external === 'bookingPage' && !bookingsLive) && <div className="oft-hint">{showing.hint}</div>}
              {!flash && status === 'skipped' && <div className="oft-state">You skipped this one.</div>}
              {!flash && status === 'done' && <div className="oft-state oft-state--done">Done.</div>}
            </div>
          </div>
          {!flash && (
            <div className="oft-actions">
              {status === 'skipped'
                ? <button type="button" className="oft-skip" onClick={() => putBack(showing.key)}>Put it back on the list</button>
                : status === 'done'
                  ? <span className="oft-where">Nothing more to do here</span>
                  : <button type="button" className="oft-skip" onClick={() => skip(showing.key)}>Not now</button>}
              <span className="oft-actions-end">
                {!showing.path && !showing.external && <span className="oft-where">On this screen</span>}
                {showing.path && <button type="button" className="oft-btn oft-btn--primary" onClick={() => history.push(showing.path)}>{showing.cta || 'Show me'}</button>}
                {showing.external && (
                  <a
                    className="oft-btn oft-btn--primary"
                    href={bookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => markFirstTask(showing.key)}
                  >{showing.cta || 'Open'}</a>
                )}
              </span>
            </div>
          )}
        </>
      )}

      {finished && (
        <>
          <div className="oft-body">
            <div className="oft-text">
              <div className="oft-title">Ready to take bookings?</div>
              <div className="oft-copy">{done === FIRST_TASKS.length ? 'All done.' : `${done} of ${FIRST_TASKS.length} tried.`} One last step: switch your booking page on and share the link.</div>
            </div>
          </div>
          <div className="oft-actions oft-actions--end">
            {done < FIRST_TASKS.length && (
              <button type="button" className="oft-skip" onClick={() => setViewKey((FIRST_TASKS.find((t) => tasks[t.key] === 'skipped') || FIRST_TASKS[0]).key)}>See what you skipped</button>
            )}
            <button type="button" className="oft-btn oft-btn--ghost" onClick={hide}>Not yet</button>
            <button type="button" className="oft-btn oft-btn--primary" onClick={() => openLayer({ mode: 'setup', screen: 'live' })}>Go live</button>
          </div>
        </>
      )}

      <ol className="oft-dots">
        {FIRST_TASKS.map((t, i) => (
          <li key={t.key}>
            <button
              type="button"
              className={`oft-dot${tasks[t.key] === 'done' ? ' is-done' : tasks[t.key] === 'skipped' ? ' is-skipped' : ''}${t.key === (showing || {}).key ? ' is-current' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`${t.title}${tasks[t.key] === 'done' ? ', done' : tasks[t.key] === 'skipped' ? ', skipped' : ''}`}
              title={t.title}
            />
          </li>
        ))}
      </ol>

      {canUndo && (
        <div className="oft-undo">
          <IconWarn size={13} />
          <span>You can undo your import for 72 hours.</span>
          <button type="button" onClick={undo} disabled={undoing}>{undoing ? 'Undoing…' : 'Undo import'}</button>
        </div>
      )}
    </section>
  );
}
